import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  keepPreviousData,
  useQueryClient,
} from '@tanstack/react-query';
import { styled } from '@mui/material/styles';
import {
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { debounce } from 'lodash';
import { useLocation, useSearchParams } from 'react-router';
import { fetchComplianceCoverage } from '@/lib/api';
import { assetQueryKeys } from '@/lib/assetQueryKeys';
import {
  ComplianceCoverageFileType,
  ComplianceCoverageResponse,
  ComplianceCoverageRow,
} from '@/lib/types';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';
import SearchTextField from '@/components/SearchTextField/SearchTextField';
import Select from '@/components/Select/Select';
import Button from '@/components/Button/Button';
import QueryState from '@/components/QueryState/QueryState';
import ComplianceDocumentsUploadModal from '@/components/ComplianceDocumentsUploadModal/ComplianceDocumentsUploadModal';
import UploadIcon from '@/assets/upload.svg?react';
import ComplianceCoverageExportButton from './ComplianceCoverageExportButton';
import ComplianceCoverageGrid from './ComplianceCoverageGrid';
import ComplianceCoverageLegend from './ComplianceCoverageLegend';
import {
  buildCoverageSearchParams,
  COVERAGE_MIN_SEARCH_LENGTH,
  COVERAGE_PAGE_SIZE,
  COVERAGE_SEARCH_DEBOUNCE_MS,
  CoverageQuery,
  getMarketLabel,
  getVisibleMarkets,
  parseCoverageSearchParams,
} from './ComplianceCoverageAdminPageHelpers';

const extractItems = (response: ComplianceCoverageResponse) =>
  response.items;

const ComplianceCoverageAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { q, marketId, fileType } = useMemo(
    () => parseCoverageSearchParams(searchParams),
    [searchParams],
  );
  const [searchInput, setSearchInput] = useState(q);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const handleUploadModalClose = () => {
    setUploadModalOpen(false);
    queryClient.invalidateQueries({
      queryKey: assetQueryKeys.compliance_documents.coverage,
    });
  };

  const updateQuery = useCallback(
    (patch: Partial<CoverageQuery>) =>
      setSearchParams(
        (current) =>
          buildCoverageSearchParams({
            ...parseCoverageSearchParams(current),
            ...patch,
          }),
        { replace: true },
      ),
    [setSearchParams],
  );

  // setSearchParams changes on every URL change. The debounced search
  // reads it through a ref so it is created once, and another filter
  // change doesn't cancel a search that is still pending.
  const updateQueryRef = useRef(updateQuery);
  useEffect(() => {
    updateQueryRef.current = updateQuery;
  }, [updateQuery]);

  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        const trimmed = value.trim();
        updateQueryRef.current({
          q:
            trimmed.length >= COVERAGE_MIN_SEARCH_LENGTH
              ? trimmed
              : '',
        });
      }, COVERAGE_SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const clearSearch = () => {
    applySearch.cancel();
    setSearchInput('');
    updateQuery({ q: '' });
  };

  useEffect(() => {
    if (gridScrollRef.current) gridScrollRef.current.scrollTop = 0;
  }, [q, marketId, fileType]);

  const {
    data: rows,
    firstPage,
    total_count,
    isPending,
    isError,
    isFetching,
    isFetchingNextPage,
    failureCount,
    refetch,
  } = useInfiniteScroll<
    ComplianceCoverageRow,
    Parameters<typeof fetchComplianceCoverage>[0],
    ComplianceCoverageResponse
  >({
    fetchFn: fetchComplianceCoverage,
    queryKey: [
      ...assetQueryKeys.compliance_documents.coverage,
      q,
      marketId ?? null,
      fileType,
    ],
    params: {
      document_type: fileType,
      search: q || undefined,
      market_id: marketId,
    },
    scrollContainerRef: gridScrollRef,
    pageSize: COVERAGE_PAGE_SIZE,
    scrollThreshold: 200,
    extractData: extractItems,
    queryOptions: {
      staleTime: 0,
      placeholderData: keepPreviousData,
    },
  });

  // The previous rows stay on screen while a new search or filter
  // loads; next pages have their own loading row in the grid.
  const isRefreshing = isFetching && !isFetchingNextPage;

  const markets = useMemo(
    () => firstPage?.markets ?? [],
    [firstPage],
  );
  const marketOptions = useMemo(
    () =>
      markets.map((market) => ({
        id: market.id,
        value: market.id,
        label: getMarketLabel(market.name),
        text: getMarketLabel(market.name),
      })),
    [markets],
  );

  return (
    <Root>
      <div className="page-width-wrapper">
        <Main>
          <Header>
            <div>
              <Title>
                <FormattedMessage
                  id="complianceCoverage.title"
                  defaultMessage="Compliance Coverage"
                />
              </Title>
              <Subtitle>
                <FormattedMessage
                  id="complianceCoverage.subtitle"
                  defaultMessage="{fileType, select, approval {Approval} other {Certificates}} per game variant and market"
                  values={{ fileType }}
                />
              </Subtitle>
            </div>
            <HeaderActions>
              <ComplianceCoverageExportButton
                q={q}
                marketId={marketId}
              />
              <Button
                variant="contained"
                onClick={() => setUploadModalOpen(true)}
                aria-label="Upload Compliance Documents Button"
              >
                <UploadIcon />
                <FormattedMessage
                  id="uploadComplianceDocuments"
                  defaultMessage="Upload Compliance Documents"
                />
              </Button>
            </HeaderActions>
          </Header>
          <Card>
            <Toolbar>
              <FileTypeToggle
                exclusive
                size="small"
                value={fileType}
                aria-label="File Type"
                onChange={(
                  _event,
                  value: ComplianceCoverageFileType | null,
                ) => {
                  if (value) updateQuery({ fileType: value });
                }}
              >
                <ToggleButton value="certificates">
                  <FormattedMessage
                    id="certificates"
                    defaultMessage="Certificates"
                  />
                </ToggleButton>
                <ToggleButton value="approval">
                  <FormattedMessage
                    id="approval"
                    defaultMessage="Approval"
                  />
                </ToggleButton>
              </FileTypeToggle>
              <SearchWrapper>
                <SearchTextField
                  placeholder={
                    <FormattedMessage
                      id="complianceCoverage.searchPlaceholder"
                      defaultMessage="Search by game name or Game Studio ID"
                    />
                  }
                  aria-label="Search game variants"
                  value={searchInput}
                  onChange={(value) => {
                    setSearchInput(value);
                    applySearch(value);
                  }}
                  onCancel={clearSearch}
                  minQueryLength={COVERAGE_MIN_SEARCH_LENGTH}
                />
              </SearchWrapper>
              <FilterWrapper>
                <Select
                  size="small"
                  searchEnabled
                  showClear={marketId !== undefined}
                  aria-label="Filter by market"
                  placeholder={
                    <FormattedMessage
                      id="complianceCoverage.allMarkets"
                      defaultMessage="Market: All"
                    />
                  }
                  options={marketOptions}
                  value={marketId ?? null}
                  onChange={(value) =>
                    updateQuery({
                      marketId:
                        typeof value === 'number' ? value : undefined,
                    })
                  }
                />
              </FilterWrapper>
            </Toolbar>
            {firstPage ? (
              <CounterRow>
                <span>
                  <strong>
                    <FormattedMessage
                      id="complianceCoverage.counter.variants"
                      defaultMessage="{count, plural, one {# game variant is} other {# game variants are}} missing {fileType, select, approval {approvals} other {certificates}}"
                      values={{
                        count: firstPage.variants_missing,
                        fileType,
                      }}
                    />
                  </strong>{' '}
                  <FormattedMessage
                    id="complianceCoverage.counter.cells"
                    defaultMessage="— {count, plural, one {# market cell} other {# market cells}} empty"
                    values={{
                      count: firstPage.cells_missing,
                    }}
                  />
                </span>
                <Shown>
                  <FormattedMessage
                    id="complianceCoverage.showing"
                    defaultMessage="Showing {shown} of {total} game variants"
                    values={{
                      shown: total_count ?? 0,
                      total: firstPage.total_variants,
                    }}
                  />
                </Shown>
              </CounterRow>
            ) : null}
            <QueryState
              query={{
                data: firstPage,
                isPending,
                isError,
                isFetching,
                failureCount,
                refetch,
              }}
              errorMessage={
                <FormattedMessage
                  id="complianceCoverage.loadError"
                  defaultMessage="We couldn't load compliance coverage. Please try again."
                />
              }
              embedded
            >
              <Results aria-busy={isRefreshing}>
                {rows.length === 0 ? (
                  <EmptyState>
                    <FormattedMessage
                      id="complianceCoverage.noMatch"
                      defaultMessage="No game variants match the search and market filter"
                    />
                  </EmptyState>
                ) : (
                  <>
                    <ComplianceCoverageGrid
                      rows={rows}
                      markets={getVisibleMarkets(markets, marketId)}
                      fileType={fileType}
                      returnTo={location.pathname + location.search}
                      scrollRef={gridScrollRef}
                      loadingMore={isFetchingNextPage}
                    />
                    <ComplianceCoverageLegend />
                  </>
                )}
                {isRefreshing ? (
                  <RefreshingOverlay
                    role="status"
                    aria-label="Updating compliance coverage"
                  >
                    <CircularProgress />
                  </RefreshingOverlay>
                ) : null}
              </Results>
            </QueryState>
          </Card>
        </Main>
      </div>
      <ComplianceDocumentsUploadModal
        open={uploadModalOpen}
        initialShowOperator
        initialShowMarkets
        onClose={handleUploadModalClose}
        onSave={handleUploadModalClose}
      />
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '100%',
  background: theme.palette.grey50,

  '> *': {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100vh - ${NavigationHeight}px)`,
    overflowY: 'auto',
  },
}));

// Fills the viewport so the grid scrolls inside the card, keeping the
// toolbar and counter in view.
const Main = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 560,
  width: '100%',
  padding: `0 ${theme.spacing(4)} ${theme.spacing(4)}`,
}));

const Header = styled('div')(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginTop: 40,
  marginBottom: theme.spacing(2),
}));

const HeaderActions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
}));

const Title = styled('div')(({ theme }) => ({
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  marginBottom: 8,
}));

const Subtitle = styled('div')(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.textSecondaryDarkGrey,
}));

const Card = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  background: theme.palette.white,
  border: `1px solid ${theme.palette.grey200}`,
  borderRadius: theme.radii.sm,
  overflow: 'hidden',
}));

const Toolbar = styled('div')(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  padding: theme.spacing(1.75, 2.25),
  borderBottom: `1px solid ${theme.palette.grey200}`,
}));

const FileTypeToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  flexShrink: 0,
  marginTop: theme.spacing(1),
  height: 36,

  '& .MuiToggleButton-root': {
    padding: theme.spacing(0, 1.75),
    fontSize: 14,
    textTransform: 'none',
    color: theme.palette.textPrimaryBlack,
    borderColor: theme.palette.grey200,
  },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover':
    {
      background: theme.palette.textPrimaryBlack,
      color: theme.palette.white,
    },
}));

const SearchWrapper = styled('div')({
  flex: 1,
  minWidth: 240,
  maxWidth: 420,
});

const FilterWrapper = styled('div')(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  marginTop: theme.spacing(1),

  // Select is sized for forms; shrink it to match the small search field.
  '& > div': {
    minWidth: 0,
    height: 36,
  },
  '& [role="button"]': {
    padding: theme.spacing(0.75, 1, 0.75, 1.5),
    fontSize: 14,
  },
  '& [role="button"] div': {
    fontSize: 14,
  },
}));

const CounterRow = styled('div')(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: theme.spacing(1, 2),
  padding: theme.spacing(1.5, 2.25),
  fontSize: 15,
  color: theme.palette.textPrimaryBlack,
}));

const Shown = styled('span')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.textSecondaryDarkGrey,
}));

// Takes the card's remaining height so the grid keeps scrolling inside
// it, and anchors the refreshing overlay.
const Results = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
});

// Same treatment as the shared Table's loader: dims the stale rows and
// sits above the grid's sticky headers.
const RefreshingOverlay = styled('div')({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  zIndex: 10,
});

const EmptyState = styled('div')(({ theme }) => ({
  padding: theme.spacing(7, 3),
  textAlign: 'center',
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.textPrimaryBlack,
  borderTop: `1px solid ${theme.palette.grey200}`,
}));

export default ComplianceCoverageAdminPage;
